import { 
    Box, Button, Typography, IconButton, TextField,
    Paper, FormControl, FormControlLabel, Radio, RadioGroup
} from '@mui/material';
import { 
    Add as AddIcon, 
    Delete as DeleteIcon
} from '@mui/icons-material';
import { Question } from '../models/question';
import { toast } from 'react-toastify';

interface AppQuestionEditorProps {
    questions: Question[];
    onQuestionsChange: (questions: Question[]) => void;
    title?: string;
}

export default function AppQuestionEditor({
    questions,
    onQuestionsChange,
    title = "Edycja pytań testowych"
}: AppQuestionEditorProps) {

    const handleQuestionTextChange = (questionIndex: number, text: string) => {
        const updatedQuestions = questions.map((q, i) => 
            i === questionIndex ? { ...q, text } : q
        );
        onQuestionsChange(updatedQuestions);
    };

    const handleOptionChange = (questionIndex: number, optionIndex: number, text: string) => {
        const updatedQuestions = questions.map((q, i) => {
            if (i === questionIndex) {
                const newOptions = [...q.options];
                newOptions[optionIndex] = text;
                return { ...q, options: newOptions };
            }
            return q;
        });
        onQuestionsChange(updatedQuestions);
    };

    const handleCorrectAnswerChange = (questionIndex: number, correctOptionIndex: number) => {
        const updatedQuestions = questions.map((q, i) => 
            i === questionIndex ? { ...q, correctOptionIndex } : q
        );
        onQuestionsChange(updatedQuestions);
    };

    const handleAddOption = (questionIndex: number) => {
        const updatedQuestions = questions.map((q, i) => {
            if (i === questionIndex) {
                return { ...q, options: [...q.options, ""] };
            }
            return q;
        });
        onQuestionsChange(updatedQuestions);
    };

    const handleRemoveOption = (questionIndex: number, optionIndex: number) => {
        if (questions[questionIndex].options.length <= 2) {
            toast.warning("Pytanie musi mieć co najmniej dwie opcje odpowiedzi");
            return;
        }
        
        const updatedQuestions = questions.map((q, i) => {
            if (i === questionIndex) {
                const newOptions = q.options.filter((_, idx) => idx !== optionIndex);
                const newCorrectOptionIndex = q.correctOptionIndex >= optionIndex && q.correctOptionIndex > 0
                    ? q.correctOptionIndex - 1
                    : q.correctOptionIndex >= newOptions.length
                        ? 0
                        : q.correctOptionIndex;
                
                return { 
                    ...q, 
                    options: newOptions,
                    correctOptionIndex: newCorrectOptionIndex
                };
            }
            return q;
        });
        
        onQuestionsChange(updatedQuestions);
    };

    const handleAddQuestion = () => {
        const newId = questions.length > 0 
            ? (parseInt(questions[questions.length - 1].id) + 1).toString()
            : "1";
        
        const newQuestion = {
            id: newId,
            text: "",
            options: ["", ""],
            correctOptionIndex: 0
        };
        
        onQuestionsChange([...questions, newQuestion]);
    };

    const handleRemoveQuestion = (questionIndex: number) => {
        if (questions.length <= 1) {
            toast.warning("Nie można usunąć wszystkich pytań");
            return;
        }
        
        const updatedQuestions = questions.filter((_, i) => i !== questionIndex);
        onQuestionsChange(updatedQuestions);
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Sekcja edycji pytań */}
            <Box sx={{ width: '100%' }}>
                <Typography variant="h5" gutterBottom>
                    {title}
                </Typography>
                {questions.map((question, questionIndex) => (
                    <Paper key={question.id || questionIndex} sx={{ p: 2, mb: 3, position: 'relative' }}>
                        <TextField
                            fullWidth
                            label={`Pytanie ${questionIndex + 1}`}
                            value={question.text || ''}
                            onChange={(e) => handleQuestionTextChange(questionIndex, e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                            <RadioGroup
                                value={(question.correctOptionIndex ?? 0).toString()}
                                onChange={(e) => handleCorrectAnswerChange(questionIndex, parseInt(e.target.value))}
                            >
                                {(question.options || []).map((option, optionIndex) => (
                                    <Box key={optionIndex} sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                                        <FormControlLabel
                                            value={optionIndex.toString()}
                                            control={<Radio />}
                                            label=""
                                        />
                                        <TextField
                                            fullWidth
                                            label={`Odpowiedź ${optionIndex + 1}`}
                                            value={option || ''}
                                            onChange={(e) => handleOptionChange(questionIndex, optionIndex, e.target.value)}
                                        />
                                        {(question.options || []).length > 2 && (
                                            <IconButton
                                                onClick={() => handleRemoveOption(questionIndex, optionIndex)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))}
                            </RadioGroup>
                        </FormControl>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Button
                                startIcon={<AddIcon />}
                                onClick={() => handleAddOption(questionIndex)}
                                variant="outlined"
                            >
                                Dodaj odpowiedź
                            </Button>
                            {questions.length > 1 && (
                                <Button
                                    startIcon={<DeleteIcon />}
                                    onClick={() => handleRemoveQuestion(questionIndex)}
                                    variant="outlined"
                                    color="error"
                                >
                                    Usuń pytanie
                                </Button>
                            )}
                        </Box>
                    </Paper>
                ))}
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddQuestion}
                        variant="contained"
                        color="primary"
                    >
                        Dodaj nowe pytanie
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}